import BotonManualHeader from "../../components/BotonManualHeader";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Container, Form, InputGroup, Row, Spinner, Table } from "react-bootstrap";
import { FaSearch, FaUserEdit, FaUserShield, FaUsers } from "react-icons/fa";
import Swal from "sweetalert2";
import firebaseApp from "../../firebase/credenciales";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";

const db = getFirestore(firebaseApp);
const GRADIENT = "linear-gradient(135deg, #4c1d95 0%, #7c3aed 60%, #a78bfa 100%)";
const PURPLE100 = "#ede9fe";
const PURPLE700 = "#6d28d9";

const normalizeRole = (rawRole) => {
  const value = String(rawRole || "").trim();
  if (!value) return "";
  const lower = value.toLowerCase();
  if (lower === "administrador") return "admin";
  if (lower === "super administrador") return "superAdmin";
  if (lower === "recursos humanos") return "rh";
  if (lower === "it") return "ti";
  if (lower === "administrador de nomina" || lower === "administrador de nómina" || lower === "nomina") return "admin_nomina";
  return value;
};

function UsuariosConRoles({ usuario }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("todos");

  const tenantId = String(usuario?.ruc || "").trim();

  const loadUsers = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("rucEmpresa", "==", tenantId)));
      const rows = snap.docs
        .map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            dni: String(data.dni || ""),
            name: String(data.name || ""),
            apellidos: String(data.apellidos || ""),
            email: String(data.email || ""),
            rol: normalizeRole(data.rol),
            activo: data.activo !== false,
          };
        })
        .filter((u) => {
          const role = String(u.rol || "").trim().toLowerCase();
          return role && role !== "user";
        })
        .sort((a, b) => `${a.apellidos} ${a.name}`.localeCompare(`${b.apellidos} ${b.name}`));
      setUsers(rows);
    } catch (e) {
      Swal.fire("Error", "No se pudo cargar usuarios con roles.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [tenantId]); // eslint-disable-line

  const roleOptions = useMemo(() => {
    const roles = Array.from(new Set(users.map((u) => String(u.rol || "").trim()).filter(Boolean)));
    return roles.sort((a, b) => a.localeCompare(b));
  }, [users]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      const matchesRole = roleFilter === "todos" || String(u.rol || "").trim() === roleFilter;
      if (!matchesRole) return false;
      if (!q) return true;
      return `${u.dni} ${u.name} ${u.apellidos} ${u.email} ${u.rol}`.toLowerCase().includes(q);
    });
  }, [users, search, roleFilter]);

  return (
    <div className="min-vh-100" style={{ background: "#f5f3ff", padding: "24px 0" }}>
      <style>{`
        .ucr-table th{font-size:.8rem;text-transform:uppercase;letter-spacing:.04em;color:#5b21b6;font-weight:800;border-bottom:2px solid #ddd6fe!important}
        .ucr-row:hover{background:#f8f5ff!important}
        .ucr-role-badge{border:1px solid #d8b4fe;background:#f5f3ff;color:#4c1d95;font-weight:700;padding:6px 10px;border-radius:999px}
      `}</style>
      <Container fluid style={{ width: "94%", maxWidth: 1450 }}>
        <Card style={{ borderRadius: 15, overflow: "hidden", boxShadow: "0 8px 32px rgba(109,40,217,.1)", border: "none" }}>
          <Card.Header className="text-white py-4 position-relative" style={{ background: GRADIENT }}>
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader />
          </div>
            <div className="text-center w-100">
              <h2 className="fw-bold mb-1">
                <FaUserShield className="me-3" size={24} />
                Usuarios con Roles Asignados
              </h2>
              <p className="mb-0 opacity-75">
                Solo usuarios con rol distinto de <strong>user</strong>
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ background: "#faf9ff" }}>
            <Row className="mb-3 g-3">
              <Col md={8} lg={6}>
                <InputGroup>
                  <InputGroup.Text style={{ background: PURPLE100, border: "1px solid #ddd6fe", borderRight: "none", color: PURPLE700 }}><FaSearch /></InputGroup.Text>
                  <Form.Control
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre, DNI, email o rol"
                    style={{ border: "1px solid #ddd6fe", borderLeft: "none" }}
                  />
                </InputGroup>
              </Col>
              <Col md={4} lg={3}>
                <Form.Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  style={{ border: "1px solid #ddd6fe", color: PURPLE700, fontWeight: 600 }}
                >
                  <option value="todos">Todos los roles</option>
                  {roleOptions.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={12} lg={3} className="d-flex align-items-center justify-content-lg-end">
                <small style={{ color: "#6b7280" }}>Tip: usa “Editar rol” para abrir Asignar Roles con ese usuario.</small>
              </Col>
            </Row>

            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" style={{ color: PURPLE700 }} /></div>
            ) : filtered.length === 0 ? (
              <Alert style={{ borderRadius: 10, border: "1px solid #ddd6fe", background: "#f5f3ff", color: "#4c1d95" }}>
                No hay usuarios con roles asignados (distintos de user) para esta empresa.
              </Alert>
            ) : (
              <div className="table-responsive" style={{ border: "1px solid #ede9fe", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 12px rgba(109,40,217,.08)" }}>
                <Table hover className="mb-0 align-middle ucr-table">
                  <thead>
                    <tr style={{ background: "linear-gradient(90deg,#ede9fe,#faf5ff)" }}>
                      <th>DNI</th>
                      <th>Usuario</th>
                      <th>Email</th>
                      <th>Rol</th>
                      <th>Estado</th>
                      <th className="text-end">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((u) => (
                      <tr key={u.id} className="ucr-row">
                        <td style={{ fontWeight: 700, color: "#4c1d95" }}>{u.dni || "-"}</td>
                        <td style={{ fontWeight: 600 }}>{`${u.apellidos} ${u.name}`.trim() || "-"}</td>
                        <td style={{ color: "#6b7280" }}>{u.email || "-"}</td>
                        <td><Badge bg="light" text="dark" className="ucr-role-badge">{u.rol}</Badge></td>
                        <td>
                          <span style={{ background: u.activo ? "linear-gradient(90deg,#059669,#34d399)" : "linear-gradient(90deg,#6b7280,#9ca3af)", color: "#fff", borderRadius: 20, padding: "4px 12px", fontWeight: 700, fontSize: ".78rem" }}>
                            {u.activo ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="text-end">
                          <Button
                            size="sm"
                            onClick={() => navigate(`/permisos-roles?dni=${encodeURIComponent(u.dni || "")}`)}
                            style={{ background: "linear-gradient(135deg,#ede9fe,#c4b5fd)", color: "#5b21b6", border: "1px solid #c4b5fd", fontWeight: 700 }}
                          >
                            <FaUserEdit className="me-1" /> Editar rol
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
}

export default UsuariosConRoles;
